import React, { useCallback, useEffect, useState } from "react";
import BillingDetail from "./BillingDetail";
import MedicineDetail from "../../components/Doctor/Dashboard/MedicineDetail";
import PatientOrderDetail from "./PatientOrderDetail";
import UploadPrescription from "./uploadPrescription";
import Button from "../../components/common/Button";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  createOrder,
  createPdf,
  updateOrderDetails,
} from "../../redux/Thunk/Order/OrderThunks";
import { frequentProductList, fetchNRxDrugs } from "../../redux/Thunk/Product/ProductThunks";
import { getAllDoctorsOfStore } from "../../redux/Thunk/Doctor/DoctorThunks";
import {
  setActiveForm,
  setActiveTab,
} from "../../redux/Reducer/Admin/PartnerSlice";
import { setLoading, setOrderById } from "../../redux/Reducer/Order/OrderSlice";
import apiAxios from "../../services/apiAxios";
import { createOrderValidation } from "../common/Validation/createOrderValidation";
import toast from "react-hot-toast";
import Loader from "../Loader";
import CloseBg from "../common/icons/CloseBg";
import Verifed from "../common/icons/Verifed";
import Download from "../common/icons/Download";
import {
  foodToggleValues,
  letters,
  productTypeValues,
} from "../../utils/constants";
import { clearActiveQuickOrder } from "../../redux/Reducer/QuickOrder/QuickOrderSlice";
import { fetchStoreQuickOrders } from "../../redux/Thunk/QuickOrder/QuickOrderThunks";

const isNRxProduct = (p, catalogProducts, nrxDrugNames = []) => {
  if (!p) return false;

  // 1. Direct product-level tags
  let rawTags = p.tags || p?.product?.tags || p?.product?.product?.tags || p?.Tag || p?.product?.Tag;
  if (!rawTags || (Array.isArray(rawTags) && rawTags.length === 0)) {
    const pIID = p.productId || p.IID || p.id;
    if (pIID && Array.isArray(catalogProducts)) {
      const found = catalogProducts.find((fp) => {
        const item = fp?.product || fp;
        return String(item?.IID || item?.id) === String(pIID);
      });
      if (found) {
        const foundItem = found.product || found;
        rawTags = foundItem.tags || foundItem.Tag;
      }
    }
  }
  if (rawTags) {
    if (typeof rawTags === "string") {
      try {
        rawTags = JSON.parse(rawTags);
      } catch (e) {
        rawTags = rawTags.split(",").map((s) => s.trim());
      }
    }
    if (!Array.isArray(rawTags)) {
      rawTags = [rawTags];
    }
    const hasProductNRx = rawTags.some((t) => {
      if (!t) return false;
      const name = typeof t === "string" ? t : t?.tagName || t?.name || t?.tag;
      const norm = name?.toString()?.trim()?.toUpperCase();
      return norm === "NRX/H1" || norm === "NRX";
    });
    if (hasProductNRx) return true;
  }

  // 2. Direct drugTags on product
  let rawDrugTags = p.drugTags || p?.product?.drugTags || p?.product?.product?.drugTags;
  if (Array.isArray(rawDrugTags) && rawDrugTags.length > 0) {
    const hasDrugNRxTag = rawDrugTags.some((t) => {
      const name = typeof t === "string" ? t : t?.tagName || t?.name || t?.tag;
      const norm = name?.toString()?.trim()?.toUpperCase();
      return norm === "NRX/H1" || norm === "NRX";
    });
    if (hasDrugNRxTag) return true;
  }

  // 3. Drug names matching nrxDrugNames list
  let rawDrugs = p.drugs || p?.product?.drugs || p?.product?.product?.drugs;
  if (!rawDrugs || (Array.isArray(rawDrugs) && rawDrugs.length === 0)) {
    const pIID = p.productId || p.IID || p.id;
    if (pIID && Array.isArray(catalogProducts)) {
      const found = catalogProducts.find((fp) => {
        const item = fp?.product || fp;
        return String(item?.IID || item?.id) === String(pIID);
      });
      if (found) {
        const foundItem = found.product || found;
        rawDrugs = foundItem.drugs;
      }
    }
  }
  if (Array.isArray(rawDrugs) && rawDrugs.length > 0 && Array.isArray(nrxDrugNames) && nrxDrugNames.length > 0) {
    const hasMatchingNRxDrug = rawDrugs.some((d) => {
      if (!d) return false;
      const dName = (typeof d === "string" ? d : d?.drugName || d?.name)?.toString()?.trim()?.toUpperCase();
      if (!dName) return false;
      return nrxDrugNames.some((nrx) => {
        const nrxNorm = nrx?.toString()?.trim()?.toUpperCase();
        return nrxNorm && (nrxNorm === dName || dName.includes(nrxNorm) || nrxNorm.includes(dName));
      });
    });
    if (hasMatchingNRxDrug) return true;
  }

  return false;
};

const StoreEditor = ({ onClose }) => {
  const dispatch = useDispatch();
  const products = useSelector((state) => state?.product?.frequentProduct);
  const nrxDrugNames = useSelector((state) => state?.product?.nrxDrugNames || []);
  const store = useSelector((state) => state?.store?.storeDetail?.store);
  const loading = useSelector((state) => state?.order?.loading);
  const doctors = useSelector(
    (state) => state?.doctor?.doctorAssociatedWithStore
  );

  const orderById = useSelector((state) => state?.order?.orderById);
  const activeForm = useSelector((state) => state?.formSwitcher?.activeForm);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [file, setFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [billingData, setBillingData] = useState(1);

  // Quick Order state
  const activeQuickOrder = useSelector((state) => state.quickOrder?.activeQuickOrder);
  const isQuickOrder = !!activeQuickOrder;

  // Clear activeQuickOrder when the editor is closed/unmounted without submitting
  useEffect(() => {
    return () => {
      dispatch(clearActiveQuickOrder());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchNRxDrugs());
    const id = store?.SID;

    let search = "";
    if (id !== undefined) {
      dispatch(frequentProductList({ id, productName: search }));
      dispatch(getAllDoctorsOfStore(id));
    }
  }, [dispatch, store?.SID]);

  useEffect(() => {
    if (Object.keys(orderById)?.length > 0) {
      setSelectedFile(orderById?.filePath);
      setFileName(orderById?.filePath);
    }
  }, [orderById]);

  // Populate selectedFile and fileName from Quick Order
  useEffect(() => {
    if (activeQuickOrder?.qoUploadUrl) {
      setSelectedFile(activeQuickOrder.qoUploadUrl);
      setFileName(activeQuickOrder.qoUploadUrl);
    }
  }, [activeQuickOrder]);

  // Auto-select doctor when opening from Quick Order
  useEffect(() => {
    const targetDID = activeQuickOrder?.DID || orderById?.DID;
    if (targetDID && doctors?.[0]?.doctors?.length > 0 && (!selectedDoctor || !selectedDoctor.DID)) {
      const matched = doctors[0].doctors.find((d) => d.DID === targetDID);
      if (matched) {
        setSelectedDoctor(matched);
      }
    }
  }, [activeQuickOrder, doctors, orderById, selectedDoctor]);


  const initialValues = {
    patient: {
      PID: activeQuickOrder?.patientDetail?.PID || orderById?.patientDetail?.PID || orderById?.PID || "",
      phoneNumber: activeQuickOrder?.patientDetail?.phoneNumber || orderById?.patientDetail?.phoneNumber || orderById?.phoneNumber || "",
      name: activeQuickOrder?.patientDetail?.name || orderById?.patientDetail?.name || orderById?.name || "",
      surname: activeQuickOrder?.patientDetail?.surname || orderById?.patientDetail?.surname || orderById?.surname || "",
      gender: activeQuickOrder?.patientDetail?.gender || orderById?.patientDetail?.gender || orderById?.gender || "",
      dateofbirth: activeQuickOrder?.patientDetail?.dateofbirth || orderById?.patientDetail?.dateofbirth || orderById?.dateofbirth || "",
      age: activeQuickOrder?.patientDetail?.age || orderById?.patientDetail?.age || orderById?.age || "",
      pincode: activeQuickOrder?.patientDetail?.pincode || orderById?.patientDetail?.pincode || orderById?.pincode || orderById?.patientAddress?.pincode || "",
      city: activeQuickOrder?.patientDetail?.city || orderById?.patientDetail?.city || orderById?.city || orderById?.patientAddress?.city || "",
    },
    products: orderById?.orderProducts?.filter(p => !p.isReturn)?.length
      ? orderById?.orderProducts?.filter(p => !p.isReturn)?.map((product) => ({
          productId: product?.IID || null,
          isCustom: !product?.IID || String(product.IID).startsWith("CP"),
          productName: product?.productName || "",
          units: Number(product?.units) || 0,
          orderUnits: activeForm === "Edit"
            ? (Number(product?.orderUnits) || 0)
            : (() => {
                const packSize = Number(product?.units || product?.uom || 1) || 1;
                const balanceUnits = Number(product?.balanceUnits) || 0;
                return Math.ceil(balanceUnits / packSize) * packSize;
              })(),
          rxDays: Number(product?.rxDays) || 0,
          orderQty: activeForm === "Edit"
            ? (Number(product?.orderQty) || 0)
            : (() => {
                const packSize = Number(product?.units || product?.uom || 1) || 1;
                const balanceUnits = Number(product?.balanceUnits) || 0;
                return Math.ceil(balanceUnits / packSize);
              })(),
          rxUnits: Number(product?.rxUnits) || "",
          balanceUnits: activeForm === "Edit"
            ? (Number(product?.balanceUnits) || 0)
            : 0,
          dosageNote: product?.dosageNote || "",
          mrp: product?.mrp || "",
          discount: product?.discount || "",
          gst: product?.gst || "",
          netAmt: product?.netAmt || "",
          type: product?.type || "",
          morningTime: product?.morningTime || 0,
          midDay: product?.midDay || 0,
          eveningTime: product?.eveningTime || 0,
          night: product?.night || 0,
          takenTime: product?.takenTime || "A/F",
          drugs: product?.drugs || [],
          qty: product?.qty || 0,
          uom: product?.uom || 0,
          extra: product?.extra || "",
          typeIndex: productTypeValues.indexOf(product?.type) !== -1 ? productTypeValues.indexOf(product?.type) : 0,
          takenTimeIndex: foodToggleValues.indexOf(product?.takenTime) !== -1 ? foodToggleValues.indexOf(product?.takenTime) : 0,
          extraIndex: letters.indexOf(product?.extra) !== -1 ? letters.indexOf(product?.extra) : 0,
          rxDaysDisplay: product?.dosageMode === "NA" || product?.isNA || product?.rxDaysDisplay === "NA" ? "NA" : (product?.rxDays ? String(product?.rxDays) : ""),
          lookupMargin: product?.product?.lookupMargin || 0,
          dosageMode: product?.dosageMode || (product?.isSOS ? "SOS" : product?.isNA ? "NA" : product?.isReducingDose ? "REDUCING" : product?.isPeriodic ? "PERIODIC" : "REGULAR"),
          isSOS: product?.isSOS || false,
          isNA: product?.isNA || false,
          isReducingDose: product?.isReducingDose || false,
          isPeriodic: product?.isPeriodic || false,
          periodicFrequency: product?.periodicFrequency || "Once a week",
          reducingDoses: Array.isArray(product?.reducingDoses) ? product?.reducingDoses.map((step, sIdx) => ({
            startDay: Number(step.startDay) || (sIdx === 0 ? 1 : 0),
            endDay: Number(step.endDay) || 0,
            rxDays: Number(step.rxDays) || 0,
            morningTime: Number(step.morningTime) || 0,
            midDay: Number(step.midDay) || 0,
            eveningTime: Number(step.eveningTime) || 0,
            night: Number(step.night) || 0
          })) : []
        }))
      : [
          {
            productId: "",
            isCustom: false,
            productName: "",
            units: 0,
            rxDays: 0,
            orderQty: 0,
            rxUnits: "",
            orderUnits: 0,
            balanceUnits: "",
            dosageNote: "",
            mrp: "",
            discount: "",
            gst: "",
            netAmt: "",
            type: "",
            typeIndex: "",
            morningTime: 0,
            midDay: 0,
            eveningTime: 0,
            night: 0,
            takenTime: "A/F",
            takenTimeIndex: 0,
            extra: 0,
            extraIndex: 0,
            drugs: [],
            qty: 0,
            uom: 0,
            dosageMode: "REGULAR",
            isSOS: false,
            isNA: false,
            isReducingDose: false,
            isPeriodic: false,
            periodicFrequency: "Once a week",
            reducingDoses: [],
            rxDaysDisplay: ""
          },
        ],
    returnProducts: orderById?.orderProducts?.filter(p => p.isReturn)?.map(product => ({
      productId: product?.IID || null,
      IID: product?.IID || null,
      isCustom: !product?.IID || String(product.IID).startsWith("CP"),
      productName: product?.productName || "",
      orderQty: Number(product?.orderQty) || 1,
      mrp: Number(product?.mrp) || 0,
      discount: Number(product?.discount) || 0,
      gst: Number(product?.gst) || 0,
      netAmt: Number(product?.netAmt) || 0,
      type: product?.type || "Tablet",
      units: Number(product?.units) || 1,
      isReturn: true,
    })) || [],

    billing: {
      subTotal: activeForm === "Edit" ? (orderById?.billing?.subTotal || 0) : 0,
      totalDiscount: activeForm === "Edit" ? (orderById?.billing?.totalDiscount || 0) : 0,
      totalReturnAmount: activeForm === "Edit"
        ? (Number(orderById?.billing?.totalReturnAmount) || (() => {
            let sum = 0;
            (orderById?.orderProducts || []).filter(p => p.isReturn).forEach(rp => {
              const mrp = Number(rp.mrp) || 0;
              const qty = Number(rp.orderQty) || 0;
              const disc = Number(rp.discount) || 0;
              const unit = disc > 0 ? (mrp - (mrp * disc) / 100) : mrp;
              sum += unit * qty;
            });
            return parseFloat(sum.toFixed(2));
          })())
        : 0,
      handlingFee: activeForm === "Edit" ? (orderById?.billing?.handlingFee || 0) : 0,
      smallCartFee: activeForm === "Edit" ? (orderById?.billing?.smallCartFee || 0) : 0,
      deliveryCharges: activeForm === "Edit" ? (orderById?.billing?.deliveryCharges || 0) : 0,
      gst: activeForm === "Edit" ? (orderById?.billing?.gst || 0) : 0,
      payableAmount: activeForm === "Edit" ? (orderById?.billing?.payableAmount || 0) : 0,
    },
    delivery: {
      isAddress: activeQuickOrder
        ? activeQuickOrder.deliveryMethod === "isAddress"
        : (orderById?.isAddress || false),
      isCollect: activeQuickOrder
        ? activeQuickOrder.deliveryMethod === "isCollect"
        : (orderById?.isCollect || false),
      isClinic: activeQuickOrder
        ? (activeQuickOrder.deliveryMethod ? activeQuickOrder.deliveryMethod === "isClinic" : true)
        : ((Object.keys(orderById || {}).length > 0) ? (orderById?.isClinic || false) : true),
      address: {
        pincode: orderById?.deliveryDetail?.pincode || "",
        city: orderById?.deliveryDetail?.city || "",
        state: orderById?.deliveryDetail?.state || "",
        areaRoad: orderById?.deliveryDetail?.areaRoad || "",
        addressType: orderById?.deliveryDetail?.addressType || "",
        premisesName: orderById?.deliveryDetail?.premisesName || "",
        premisesNoFloor: orderById?.deliveryDetail?.premisesNoFloor || "",
        landmark: orderById?.deliveryDetail?.landmark || "",
        phoneNumber2: orderById?.deliveryDetail?.phoneNumber || ""
      }
    },
    payment: activeQuickOrder?.paymentMethod || orderById?.payment || "upi",
    DID: activeQuickOrder?.DID || orderById?.DID || "",
    XID: orderById?.XID || null,
    QID: activeQuickOrder?.QID || null,
  };
  // const SetFormikValues = ({ orderById }) => {
  //   const { setValues, resetForm } = useFormikContext();
  //   const updateOrderDetailsValues = useCallback(() => {
  //     if (Object.keys(orderById)?.length > 0 && !isUploading) {
  //       const orderDetails = orderById;
  //       setValues({
  //         patient: {
  //           phoneNumber: orderDetails?.patientDetail?.phoneNumber || "",
  //           name: orderDetails?.patientDetail?.name || "",
  //           surname: orderDetails?.patientDetail?.surname || "",
  //         },
  //         products: orderDetails?.orderProducts?.map((product) => ({
  //           productId: product?.IID,
  //           productName: product?.productName || "",
  //           units: Number(product?.units) || 0,
  //           orderUnits: Number(product?.orderUnits) || 0,
  //           rxDays: Number(product?.rxDays) || 0,
  //           orderQty: Number(product?.orderQty) || 0,
  //           rxUnits: Number(product?.rxUnits) || "",
  //           balanceUnits: Number(product?.balanceUnits) || "",
  //           dosageNote: product?.dosageNote || "",
  //           mrp: product?.mrp || "",
  //           discount: product?.discount || "",
  //           gst: product?.gst || "",
  //           netAmt: product?.netAmt || "",
  //           type: product?.type || "",
  //           morningTime: product?.morningTime || 0,
  //           midDay: product?.midDay || 0,
  //           eveningTime: product?.eveningTime || 0,
  //           night: product?.night || 0,
  //           takenTime: product?.takenTime || "A/F",
  //           drugs: product?.drugs || [],
  //           qty: product?.qty || 0,
  //           uom: product?.uom || 0,
  //         })),
  //         billing: {
  //           subTotal: orderDetails?.billing?.subTotal || 0,
  //           totalDiscount: orderDetails?.billing?.totalDiscount || 0,
  //           handlingFee: orderDetails?.billing?.handlingFee || 0,
  //           smallCartFee: orderDetails?.billing?.smallCartFee || 0,
  //           deliveryCharges: orderDetails?.billing?.deliveryCharges || 0,
  //           gst: orderDetails?.billing?.gst || 0,
  //           payableAmount: orderDetails?.billing?.payableAmount || 0,
  //         },
  //         delivery: {
  //           isAddress: orderDetails?.isAddress || false,
  //           isCollect: orderDetails?.isCollect || false,
  //           isClinic: orderDetails?.isClinic || false,
  //         },
  //         payment: orderDetails?.payment || "",
  //         DID: orderDetails?.DID || "",
  //       });
  //     } else {
  //       // resetForm();
  //     }
  //   }, [orderById, setValues]);

  //   useEffect(() => {
  //     updateOrderDetailsValues();
  //   }, [updateOrderDetailsValues]);
  // };

  const findSelectedDocter = (DID) =>{
    const data =  doctors[0]?.doctors?.find((dct) => dct.DID === DID)
    return {
      autoSignature: data?.autoSignature,
    }
  };

  const updateFileValues = useCallback((selectedFile) => {
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile?.name);
      setError(null);
    } else {
      setFileName("");
      setError("Please select a file first.");
    }
  }, []);

  const handleUpload = useCallback(
    async (selectedFile) => {
      if (!selectedFile) {
        setError("Please select a file first.");
        return; // Exit if no file is selected
      }

      updateFileValues(selectedFile);
      dispatch(setLoading(true));
      setUploading(true);
      const formData = new FormData();
      formData.append("image", selectedFile);

      try {
        const response = await apiAxios.post(
          "api/order/uploadImage",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        const filePath = response?.data?.filePath;

        if (filePath) {
          toast.success("File uploaded successfully!");
          setSelectedFile(filePath);

          return filePath;
        } else {
          throw new Error("File path not returned from the API.");
        }
      } catch (err) {
        setError("Failed to upload file.");
        toast.error("Failed to upload file.");
        setFile("");
        setFileName("");
        return null;
      } finally {
        setUploading(false);
        dispatch(setLoading(false));
      }
    },
    [dispatch, updateFileValues]
  );

  const handleUpdate = async (values, resetForm) => {
    if (values?.hasIneligibleReturnProduct) {
      toast.error("Cannot update order: One or more return products were not ordered by this patient in the last 30 days.");
      return;
    }

    const regularProdsCheck = (values?.products || []).filter(
      (product) => product?.productName?.length > 0
    );
    if (regularProdsCheck.length === 1 && isNRxProduct(regularProdsCheck[0], products, nrxDrugNames)) {
      toast.error("Orders cannot be placed for a prescription containing only a single NRX/H1 product.");
      return;
    }

    const clonedValues = JSON.parse(JSON.stringify(values));

    if (clonedValues?.patient?.dateofbirth && String(clonedValues.patient.dateofbirth).trim().length > 0) {
      const date = new Date(clonedValues.patient.dateofbirth);
      const formattedDate = date.toLocaleDateString('en-CA');
      clonedValues.patient.dateofbirth = formattedDate;
    } else {
      clonedValues.patient.dateofbirth = null;
    }

    const removeSpecificKeys = (obj) => {
      if (Array.isArray(obj)) {
        obj.forEach((item) => removeSpecificKeys(item));
      } else if (typeof obj === "object" && obj !== null) {
        Object.keys(obj).forEach((key) => {
          if (["typeIndex", "takenTimeIndex"].includes(key)) {
            delete obj[key]; // Remove the specific keys
          } else if (typeof obj[key] === "object") {
            removeSpecificKeys(obj[key]); // Recursively handle nested objects
          }
        });
      }
      if (obj?.delivery?.isClinic || obj?.delivery?.isCollect) {
        delete obj.delivery.address;
      }
    };

    const regularProds = (clonedValues?.products || []).filter(
      (product) => product?.productName?.length > 0
    );
    const returnProds = (clonedValues?.returnProducts || [])
      .filter((product) => product?.productName?.length > 0)
      .map((product) => ({ ...product, isReturn: true }));
    clonedValues.products = [...regularProds, ...returnProds];
    removeSpecificKeys(clonedValues);

    // Preserve XID if editing an order with prescription
    if (orderById?.XID) {
      clonedValues.XID = orderById.XID;
    }

    // Attach previous order ID for refill attempt tracking if placing a new order from record
    if (activeForm !== "Edit" && orderById?.OID) {
      clonedValues.previous_OID = orderById.OID;
    }
    
    // Set source for store orders vs reorders
    clonedValues.source = (activeForm !== "Edit" && orderById?.OID) ? 'reorder' : 'store';

    try {
      const id = orderById?.OID;
      let response;

      if (activeForm === "Edit") {
        response = await dispatch(
          updateOrderDetails({ id, order: clonedValues })
        ).unwrap();
      } else {
        response = await dispatch(createOrder(clonedValues)).unwrap();
      }

      if (response?.status === 200 || response?.status === 201) {
        // If this was a Quick Order conversion, clean up
        if (isQuickOrder) {
          dispatch(clearActiveQuickOrder());
          dispatch(fetchStoreQuickOrders());
        }
        dispatch(setOrderById({}));
        setFileName("");
        setFile("");
        setSelectedFile("");
        dispatch(setOrderById({}));
        dispatch(setActiveForm(""));
        dispatch(setActiveTab({ name: "", index: "" }));
        setSelectedDoctor("");
        setCameraOn(false);
        setImageData("");
        resetForm();
        if (onClose) onClose();
        return true;
      } else {
        toast.error(response?.message);
      }

      return false;
    } catch (error) {
      console.error("Complete Error Object:", error);

      if (error?.response) {
        console.error("API Error:", error.response.data.error);
      } else {
        console.error("Unexpected Error:", error);
      }
    }
  };

  const checkValidation = (values) => {
    let errors = {};
    const { autoSignature } = findSelectedDocter(values.DID)
    if (!values.payment.length) {
      errors = `Please Select payment type!!`;
    }
    if (values?.delivery?.isAddress) {
      const requiredFields = [
        "state",
        "pincode",
        "city",
        "areaRoad",
        "premisesName",
        "addressType",
        "premisesNoFloor",
      ];

      for (const field of requiredFields) {
        if (!values?.delivery?.address?.[field]) {
          if (field === "addressType") {
            errors = `Please select the ${field.replace(
              /([A-Z])/g,
              " $1"
            )} field.`;
          } else {
            errors = `Please fill out the ${field.replace(
              /([A-Z])/g,
              " $1"
            )} field.`;
          }
        }
      }
    }
    if (
      !values.delivery.isAddress &&
      !values.delivery.isCollect &&
      !values.delivery.isClinic
    ) {
      errors = `Please Select delivery address type!!`;
    }

    if (values?.products?.length > 0) {
      values.products.forEach((product, index) => {
        if (!autoSignature && !selectedFile.length) {
          if (cameraOn) {
            errors = `Please click and upload prescription photo!!`;
          } else {
            errors = `Please upload prescription!!`;
          }
        } else {
          const isSpecialMode = product.isSOS || product.isNA || product.isReducingDose || product.isPeriodic || product.dosageMode === 'SOS' || product.dosageMode === 'NA' || product.dosageMode === 'REDUCING' || product.dosageMode === 'PERIODIC';
          const hasSlotDose = (product.morningTime || 0) > 0 || (product.midDay || 0) > 0 || (product.eveningTime || 0) > 0 || (product.night || 0) > 0;
          const isNaDuration = product.isNA || product.dosageMode === 'NA' || product.rxDaysDisplay === 'NA';

          if (!isSpecialMode && !hasSlotDose) {
            errors = `Please prescribe daily dosage for ${product.productName || 'medicine'} #${index + 1}!`;
          } else if (!isNaDuration && (!product.rxDays || product.rxDays <= 0 || isNaN(product.rxDays))) {
            errors = `Please enter RX days for ${product.productName || 'medicine'} #${index + 1}!`;
          }
          if (!product.productName) {
            errors = `Product is not selected for medicine #${index + 1}!`;
          }
        }
      });
    } else {
      errors = "Please add at least one product!!";
    }

    if (values?.DID?.length === 0) {
      errors = `Please Select Doctor!!`;
    }

    return errors;
  };

  const checkValidationForPdf = (values) => {
    let errors = {};

    if (values?.patient?.phoneNumber?.length === 0) {
      errors = `Please Select Patient!!`;
    }

    if (values?.products?.length > 0) {
      values?.products?.forEach((product, index) => {
        const isSpecialMode = product.isSOS || product.isNA || product.isReducingDose || product.isPeriodic || product.dosageMode === 'SOS' || product.dosageMode === 'NA' || product.dosageMode === 'REDUCING' || product.dosageMode === 'PERIODIC';
        const hasSlotDose = (product.morningTime || 0) > 0 || (product.midDay || 0) > 0 || (product.eveningTime || 0) > 0 || (product.night || 0) > 0;
        const isNaDuration = product.isNA || product.dosageMode === 'NA' || product.rxDaysDisplay === 'NA';

        if (!product.productName) {
          errors = `Product is not selected for medicine #${index + 1}!`;
        } else if (!isSpecialMode && !hasSlotDose) {
          errors = `Please prescribe daily dosage for ${product.productName || 'medicine'} #${index + 1}!`;
        } else if (!isNaDuration && (!product.rxDays || product.rxDays <= 0 || isNaN(product.rxDays))) {
          errors = `Please enter valid RX days for ${product.productName || 'medicine'} #${index + 1}!`;
        }
      });
    } else {
      errors = "Please add at least one product!!";
    }
    if (values?.DID?.length === 0) {
      errors = `Please Select Doctor!!`;
    }

    return errors;
  };

  const handleSubmit = async (values, { resetForm }) => {
    const data = values;
    // const filePath = await handleUpload();
    if (selectedFile.length > 0) {
      data.filePath = selectedFile;
    }

    // Attach QID if this is a Quick Order conversion
    if (activeQuickOrder?.QID) {
      data.QID = activeQuickOrder.QID;
    }

    // if (data?.delivery?.address?.addressType?.length) {
    //   delete data.delivery.address;
    // }
    const errors = checkValidation(data);

    if (Object.keys(errors).length > 0) {
      toast.error(errors);
      return;
    }

    const response = await handleUpdate(values, resetForm);

    if (response) {
      resetForm();
      // toast.success("Order submitted successfully!"); // Optional: Notify user
    }
  };

  const handlePrintPdf = async (values) => {
    const errors = checkValidationForPdf(values);

    if (Object.keys(errors).length > 0) {
      toast.error(errors);
      return;
    }
    const response = await dispatch(createPdf(values)).unwrap();
    if (response?.status === 200) {
      if (response?.data?.success && response?.data?.url) {
        const res = await fetch(response.data.url);
        values.pdfPath = response?.data?.url;
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = values?.patient?.name
          ? `${values?.patient?.name} ${values?.patient?.surname}_prescription.pdf`
          : `generated_file.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        toast.success("Pdf successfully created.");
      }
    } else {
      toast.error(response?.message);
    }
  };
  return (
    <>
      <Loader loading={loading} />
      <Formik
        initialValues={initialValues}
        validationSchema={createOrderValidation}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          handleChange,
          setFieldValue,
          touched,
          errors,
          setFieldTouched,
          handleBlur,
          dirty,
        }) => {
          const { autoSignature } = findSelectedDocter(values.DID);
          return (
            <Form className="dashboard-container">
              {/* <SetFormikValues orderById={orderById} /> */}

              {/* Quick Order Banner */}
              {isQuickOrder && (
                <div className="mb-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-orange-200 rounded-[12px] flex flex-col gap-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                      </svg>
                      <span className="text-[13px] font-extrabold text-orange-700">Quick Order</span>
                      <span className="text-[11px] font-bold bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full border border-orange-200">
                        {activeQuickOrder.QID}
                      </span>
                      <span className="text-[11px] text-gray-500">
                        {activeQuickOrder.patientDetail?.name} {activeQuickOrder.patientDetail?.surname}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.open(activeQuickOrder.qoUploadUrl, '_blank')}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 transition-colors"
                    >
                      Open Upload
                    </button>
                  </div>
                  {activeQuickOrder.qoNotes && (
                    <div className="p-2.5 bg-white rounded-lg border border-orange-100 text-[11px] text-gray-700 mt-1 shadow-sm">
                      <span className="font-bold text-orange-600 block uppercase tracking-wider text-[9px] mb-0.5">Order Notes from Doctor:</span>
                      <p className="whitespace-pre-line leading-relaxed">{activeQuickOrder.qoNotes}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-[8px] mb-2 flex-wrap">
                {/* <LastOrderDetail /> */}
                <PatientOrderDetail
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  selectedDoctor={selectedDoctor}
                  setSelectedDoctor={setSelectedDoctor}
                  touched={touched}
                  errors={errors}
                  setFieldTouched={setFieldTouched}
                  handleBlur={handleBlur}
                  readOnly={activeForm === "Edit"}
                />
              </div>
              <MedicineDetail
                setFieldValue={setFieldValue}
                values={values}
                handleChange={handleChange}
                products={products}
                billingData={billingData}
                setBillingData={setBillingData}
                readOnly={activeForm === "Edit"}
                activeTab={activeForm === "Edit" ? "open" : "create"}
                qtyEditable={true}
              />
              <div className="flex flex-wrap">
                <BillingDetail
                  setFieldValue={setFieldValue}
                  values={values}
                  handleChange={handleChange}
                  billingData={billingData}
                  setBillingData={setBillingData}
                  readOnly={false}
                />
                <div className="w-full mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <UploadPrescription
                    values={values}
                    file={file}
                    setFile={setFile}
                    uploading={uploading}
                    setUploading={setUploading}
                    error={error}
                    setError={setError}
                    handleUpload={handleUpload}
                    fileName={fileName}
                    setFileName={setFileName}
                    selectedFile={selectedFile}
                    setSelectedFile={setSelectedFile}
                    handlePrintPdf={handlePrintPdf}
                    cameraOn={cameraOn}
                    setCameraOn={setCameraOn}
                    imageData={imageData}
                    setImageData={setImageData}
                    autoSignature={autoSignature}
                    readOnly={activeForm === "Edit"}
                  />

                  <div className="border border-primary-100 p-2 rounded-[8px] bg-primary-50 sm:only:col-span-2">
                    <div className="flex items-center justify-center gap-1 mb-3">
                      <span>
                        { autoSignature ? <Verifed height={30} width={30} fill="#00C732" /> : <CloseBg height={26} width={26} fill="#8E1010" /> }
                      </span>
                      <h4 className="text-[16px] font-medium text-black-lose">Smart Order { autoSignature ? "Enabled" :  "Disbled" } </h4>
                    </div>
                    <Button
                      type="button"
                      className="text-[13px] flex items-center mb-2 justify-center gap-3 bg-[#F2EEEE] border border-primary-600 text-secondry-600 w-full p-2 rounded-[8px]"
                      onClick={() => handlePrintPdf(values)}
                    >
                      <Download />
                      Download Prescription
                    </Button>
                    {(() => {
                      const isPatientDetailsFilled =
                        values?.patient?.phoneNumber?.length === 10 &&
                        values?.patient?.name?.trim()?.length > 0;
                      const allProductsDataFilled = values?.products?.length > 0 && values?.products?.every((product) => {
                        const isSpecialMode = product?.isSOS || product?.isNA || product?.isReducingDose || product?.isPeriodic || product?.dosageMode === 'SOS' || product?.dosageMode === 'NA' || product?.dosageMode === 'REDUCING' || product?.dosageMode === 'PERIODIC';
                        const hasDosage = isSpecialMode || (product?.morningTime > 0 || product?.midDay > 0 || product?.eveningTime > 0 || product?.night > 0);
                        const isNaDuration = product?.isNA || product?.dosageMode === 'NA' || product?.rxDaysDisplay === 'NA';
                        const hasRxDays = isNaDuration ? true : (product?.rxDays > 0 && !isNaN(product?.rxDays));
                        const hasProductName = product?.productName?.length > 0;
                        const isCustom = product?.isCustom === true || (product?.productId && String(product?.productId).startsWith("CP")) || (product?.IID && String(product?.IID).startsWith("CP"));
                        const hasOrderQty = Number(product?.orderQty) > 0 || isCustom;
                        return hasDosage && hasRxDays && hasProductName && hasOrderQty;
                      });
                      const isPrescriptionUploaded = autoSignature || selectedFile?.length > 0;
                      const regularProds = (values?.products || []).filter(p => p?.productName?.trim()?.length > 0);
                      const hasSingleNRxProductBlock = regularProds.length === 1 && isNRxProduct(regularProds[0], products, nrxDrugNames);

                      const isAllDataFilled = isPatientDetailsFilled && allProductsDataFilled && isPrescriptionUploaded && values?.DID?.length > 0 && !hasSingleNRxProductBlock;
                      
                      const isEditMode = activeForm === "Edit" && Object.keys(orderById || {})?.length > 0;
                       
                      const hasChanged = (() => {
                        const originalAllProds = orderById?.orderProducts || [];
                        const originalRegularProds = originalAllProds.filter(p => !p.isReturn);
                        const originalReturnProds = originalAllProds.filter(p => p.isReturn);

                        const currentRegularProds = (values?.products || []).filter(p => p?.productName?.trim()?.length > 0);
                        const currentReturnProds = (values?.returnProducts || []).filter(p => p?.productName?.trim()?.length > 0);

                        if (originalRegularProds.length !== currentRegularProds.length) return true;
                        if (originalReturnProds.length !== currentReturnProds.length) return true;

                        for (let i = 0; i < currentRegularProds.length; i++) {
                          const current = currentRegularProds[i];
                          const original = originalRegularProds.find(op => 
                            (current.productId && op.IID === current.productId) || 
                            (current.productName && op.productName === current.productName)
                          );
                          if (!original) return true;
                          if (Number(current.orderQty) !== Number(original.orderQty)) return true;
                        }

                        for (let i = 0; i < currentReturnProds.length; i++) {
                          const current = currentReturnProds[i];
                          const original = originalReturnProds.find(op => 
                            (current.productId && op.IID === current.productId) || 
                            (current.productName && op.productName === current.productName)
                          );
                          if (!original) return true;
                          if (Number(current.orderQty) !== Number(original.orderQty)) return true;
                        }

                        if (values?.DID && orderById?.DID && values.DID !== orderById.DID) return true;
                        if (values?.payment && orderById?.payment && values.payment !== orderById.payment) return true;

                        return false;
                      })();

                      const shouldBeGreen = isEditMode ? (hasChanged && isAllDataFilled && !values?.hasIneligibleReturnProduct && !hasSingleNRxProductBlock) : (isAllDataFilled && !values?.hasIneligibleReturnProduct && !hasSingleNRxProductBlock);
                      const isDisabled = isEditMode ? (!hasChanged || Boolean(values?.hasIneligibleReturnProduct) || hasSingleNRxProductBlock) : (Boolean(values?.hasIneligibleReturnProduct) || hasSingleNRxProductBlock || !isAllDataFilled);

                      const buttonClass = shouldBeGreen
                        ? "flex text-[20px] items-center justify-center gap-3 text-white bg-success-600 w-full p-2 rounded-[8px] hover:bg-success-700 hover:border-success-800 focus:border-success-200 border-[4px] border-success-600 shadow-lg transition-all"
                        : "flex text-[20px] items-center justify-center gap-3 text-white bg-primary-600 w-full p-2 rounded-[8px] hover:bg-primary-700 hover:border-primary-800 focus:border-primary-200 border-[4px] border-primary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed";

                      return (
                        <>
                          {hasSingleNRxProductBlock && (
                            <div className="mb-2 p-2 bg-amber-50 border border-amber-300 rounded-[6px] text-amber-800 text-[11px] font-medium text-center leading-snug">
                              ⚠ Order creation blocked: Prescriptions containing only a single <strong className="font-bold">NRX/H1</strong> product cannot be submitted as an order. Please add atleast one subsidery product to place the order.
                            </div>
                          )}
                          {values.XID && (
                            <div className="flex flex-col gap-2 px-4 py-3 mb-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-xs font-semibold shadow-sm text-left">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  <span>Prescription ID:</span>
                                </div>
                                <span className="font-mono bg-white px-2.5 py-1 rounded-lg border border-slate-200 text-slate-800 text-[11px] shadow-sm select-all">
                                  {values.XID}
                                </span>
                              </div>
                              {orderById?.OID && (
                                <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                                  <div className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                    </svg>
                                    <span>{activeForm === "Edit" ? "Order ID:" : "Previous Order ID:"}</span>
                                  </div>
                                  <span className="font-mono bg-white px-2.5 py-1 rounded-lg border border-slate-200 text-slate-800 text-[11px] shadow-sm select-all">
                                    #{orderById.OID}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          <Button
                            type="submit"
                            className={buttonClass}
                            disabled={isDisabled}
                          >
                            {activeForm === "Edit" && Object.keys(orderById)?.length > 0 ? "Update Order" : "Submit Order"}
                          </Button>
                        </>
                      );
                    })()}
                    <span className="text-primary-400 font-light text-[12px] mt-2 block text-center">
                      * With patient consent, prescriptions are shared with third-party pharmacies to fulfill medication orders. Check our{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-500 underline hover:text-primary-700 font-regular"
                      >
                        Terms of Service
                      </a>{" "}
                      for more!
                    </span>
                  </div>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </>
  );
};
export default StoreEditor;
